import { useCallback, useEffect, useRef } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import {
  type RecaptchaTokenResult,
  waitForRecaptchaExecutor,
} from "~/feature/auth/recaptchaToken";

const READY_ATTEMPTS = 50;
const READY_INTERVAL_MS = 100;

const sleep = () =>
  new Promise((resolve) => setTimeout(resolve, READY_INTERVAL_MS));

export const useRecaptchaToken = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const executorRef = useRef(executeRecaptcha);

  useEffect(() => {
    executorRef.current = executeRecaptcha;
  }, [executeRecaptcha]);

  return useCallback(async (action: string): Promise<RecaptchaTokenResult> => {
    const executor = await waitForRecaptchaExecutor(
      () => executorRef.current,
      { attempts: READY_ATTEMPTS, sleep }
    );

    if (!executor) {
      return { ok: false, reason: "not-loaded" };
    }

    try {
      const token = await executor(action);

      return token ? { ok: true, token } : { ok: false, reason: "failed" };
    } catch {
      return { ok: false, reason: "failed" };
    }
  }, []);
};
